import axios from "axios";

// Saves the application form as a draft at the moments that matter, not on a timer:
//   - right after the component calls autosave() (e.g. once the dates are confirmed,
//     which also starts the 10-minute date reservation on the server),
//   - as soon as a file is picked (files are too big to send when the tab closes),
//   - when the applicant leaves: in-app navigation, closing/reloading/hiding the tab,
//     logging out, or just before the login token expires.
// Saves that aren't a date confirmation send noHold so they never start a reservation.
// Nothing is sent until the form differs from the last saved state.
//
// The component provides:
//   autosaveFields()  -> plain object of form fields (null/undefined are skipped,
//                        arrays/objects are sent as JSON)
//   autosaveFiles()   -> { field: File | string | null }; only new File objects are uploaded
//   autosaveAppId()   -> id of the draft being edited, or null before the first save
//   autosaveActive()  -> false to stop autosaving (e.g. after final submit)
//   onAutosaved(data) -> optional, receives the saved draft ({ _id, applicationNo, ... })
// and calls autosaveMarkSaved() once its form is loaded, so the loaded state is
// treated as already saved.

// Save this long before the login token expires, while it still works.
const EXPIRY_MARGIN = 2 * 60 * 1000;

export default {
  data() {
    return {
      autosaveSnackBar: false,
      autosaveSavedAt: null,
      autosaveLeaveDialog: false,
    };
  },

  computed: {
    autosaveSavedTime() {
      if (!this.autosaveSavedAt) return "";
      return this.autosaveSavedAt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
    },
  },

  created() {
    // Non-reactive bookkeeping.
    // The id in the URL, kept now because $route already points at the next page by
    // the time beforeDestroy runs.
    this.autosaveRouteId = this.$route && this.$route.query.id;
    this.autosavePromise = null;
    this.autosaveLastSnapshot = null;
    this.autosaveUploaded = {};
    this.autosaveExpiryTimer = null;
    this.autosaveUnwatchFiles = null;
    this.autosaveLeaveNext = null;
    this.autosaveDiscarded = false;
    this.autosaveExitHandler = () => {
      if (document.visibilityState === "hidden") this.autosaveKeepalive();
    };
    this.autosavePageHideHandler = () => this.autosaveKeepalive();
    this.autosaveLogoutHandler = (pending) => {
      pending.push(this.autosave({ silent: true, noHold: true }));
    };
  },

  mounted() {
    // A picked file is uploaded straight away.
    this.autosaveUnwatchFiles = this.$watch(
      () => this.autosaveFiles(),
      (files) => {
        const picked = Object.keys(files || {}).some(
          (k) => files[k] instanceof File && this.autosaveUploaded[k] !== files[k]
        );
        if (picked) this.autosave({ silent: true, noHold: true });
      }
    );
    document.addEventListener("visibilitychange", this.autosaveExitHandler);
    window.addEventListener("pagehide", this.autosavePageHideHandler);
    this.$root.$on("before-logout", this.autosaveLogoutHandler);
    this.autosaveScheduleExpiry();
  },

  // Leaving for another page in the app (back button, sidebar or top bar tab). If there
  // are unsaved changes, ask the applicant whether to save them as a draft, discard
  // them, or stay on the form.
  beforeRouteLeave(to, from, next) {
    if (!this.autosaveChanges()) return next();
    // Logout and session timeout both end on the login page. These aren't the
    // applicant's choice, so keep their work without asking.
    if (to.path === "/login") {
      return Promise.race([
        this.autosave({ silent: true, noHold: true }),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]).then(() => next());
    }
    this.autosaveLeaveNext = next;
    this.autosaveLeaveDialog = true;
  },

  beforeDestroy() {
    if (this.autosaveUnwatchFiles) this.autosaveUnwatchFiles();
    document.removeEventListener("visibilitychange", this.autosaveExitHandler);
    window.removeEventListener("pagehide", this.autosavePageHideHandler);
    this.$root.$off("before-logout", this.autosaveLogoutHandler);
    clearTimeout(this.autosaveExpiryTimer);
    // The applicant chose to discard their changes when leaving: don't save them.
    if (this.autosaveDiscarded) return;
    this.autosave({ silent: true, noHold: true });
  },

  methods: {
    autosaveLeaveSave() {
      this.autosaveLeaveDialog = false;
      const next = this.autosaveLeaveNext;
      this.autosaveLeaveNext = null;
      Promise.race([
        this.autosave({ silent: true, noHold: true }),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]).then(() => next && next());
    },

    autosaveLeaveDiscard() {
      this.autosaveLeaveDialog = false;
      this.autosaveDiscarded = true;
      const next = this.autosaveLeaveNext;
      this.autosaveLeaveNext = null;
      if (next) next();
    },

    autosaveLeaveStay() {
      this.autosaveLeaveDialog = false;
      const next = this.autosaveLeaveNext;
      this.autosaveLeaveNext = null;
      if (next) next(false);
    },

    autosaveSnapshot() {
      return JSON.stringify(this.autosaveFields());
    },

    autosaveMarkSaved() {
      this.$nextTick(() => {
        this.autosaveLastSnapshot = this.autosaveSnapshot();
        const files = this.autosaveFiles() || {};
        Object.keys(files).forEach((k) => {
          this.autosaveUploaded[k] = files[k];
        });
      });
    },

    // The login token is only valid for a while and the session ends silently when
    // it runs out, so save shortly before that happens.
    autosaveScheduleExpiry() {
      clearTimeout(this.autosaveExpiryTimer);
      let exp = 0;
      try {
        const payload = localStorage.getItem("token").split(".")[1];
        exp = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))).exp * 1000;
      } catch (e) {
        return;
      }
      const wait = exp - Date.now() - EXPIRY_MARGIN;
      if (!(wait > 0)) return;
      this.autosaveExpiryTimer = setTimeout(
        () => this.autosave({ silent: true, noHold: true }),
        Math.min(wait, 2147483647)
      );
    },

    // What changed since the last save, or null when there is nothing to send.
    autosaveChanges() {
      // Not loaded yet, or no longer a draft.
      if (this.autosaveLastSnapshot === null || !this.autosaveActive()) return null;
      const fields = this.autosaveFields();
      const snapshot = JSON.stringify(fields);
      const files = this.autosaveFiles() || {};
      const newFiles = Object.keys(files).filter(
        (k) => files[k] instanceof File && this.autosaveUploaded[k] !== files[k]
      );
      if (snapshot === this.autosaveLastSnapshot && newFiles.length === 0) return null;
      return { fields, snapshot, files, newFiles };
    },

    autosaveFormData({ fields, files, newFiles }, noHold, withFiles) {
      const formData = new FormData();
      const appid = this.autosaveAppId();
      if (appid) formData.append("appid", appid);
      if (noHold) formData.append("noHold", "true");
      Object.keys(fields).forEach((k) => {
        const v = fields[k];
        if (v === null || v === undefined) return;
        formData.append(k, typeof v === "object" ? JSON.stringify(v) : v);
      });
      if (withFiles) newFiles.forEach((k) => formData.append(k, files[k]));
      return formData;
    },

    autosaveDone(changes, data, silent) {
      this.autosaveLastSnapshot = changes.snapshot;
      changes.newFiles.forEach((k) => {
        this.autosaveUploaded[k] = changes.files[k];
      });
      this.autosaveSavedAt = new Date();
      if (this.onAutosaved) this.onAutosaved(data || {});
      if (!silent) this.autosaveSnackBar = true;
      this.autosaveScheduleExpiry();
    },

    // noHold: don't start a date reservation with this save.
    autosave({ silent = false, noHold = false } = {}) {
      // A save is already on its way. What was entered after it started isn't in it,
      // and a noHold save can't stand in for one that should start a reservation,
      // so look again once it is done (this sends nothing if nothing is left).
      if (this.autosavePromise) {
        return this.autosavePromise.then(() => this.autosave({ silent, noHold }));
      }
      const changes = this.autosaveChanges();
      if (!changes) return Promise.resolve();

      this.autosavePromise = axios({
        method: "POST",
        url: "/autosave/application",
        headers: {
          "Content-Type": "multipart/form-data",
          token: localStorage.getItem("token"),
        },
        data: this.autosaveFormData(changes, noHold, true),
      })
        .then((response) => {
          if (response.data.status == true) this.autosaveDone(changes, response.data.data, silent);
        })
        .catch((err) => {
          // A missed save is tried again the next time one is triggered; don't
          // interrupt the applicant.
          console.error(err);
        })
        .finally(() => {
          this.autosavePromise = null;
        });
      return this.autosavePromise;
    },

    // For when the tab is closing, reloading or being hidden: a normal request is
    // cancelled by the browser, a keepalive one is allowed to finish. It is capped
    // at about 64KB, so only the text fields go (files are uploaded when picked).
    autosaveKeepalive() {
      if (this.autosavePromise) return this.autosavePromise;
      // Files aren't sent here, so they must not be marked as uploaded.
      const changes = this.autosaveChanges();
      if (!changes || changes.snapshot === this.autosaveLastSnapshot) return Promise.resolve();
      changes.newFiles = [];

      this.autosavePromise = fetch((axios.defaults.baseURL || "") + "/autosave/application", {
        method: "POST",
        keepalive: true,
        credentials: "include",
        headers: {
          token: localStorage.getItem("token") || "",
          "X-Device-Id": localStorage.getItem("device_id") || "",
        },
        body: this.autosaveFormData(changes, true, false),
      })
        .then((response) => response.json())
        .then((body) => {
          // Only matters if the page is still there (tab switch, cancelled reload).
          if (body && body.status == true) this.autosaveDone(changes, body.data, true);
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.autosavePromise = null;
        });
      return this.autosavePromise;
    },
  },
};
